/**
 * Asset type checking utilities
 *
 * This module provides type-safe utilities for checking asset types.
 * Functions support both FileRecordType (legacy) and Asset (new) types.
 */

import type {FileRecordType} from '@packages/systems/core/records/FileRecord';
import type {Asset} from '@packages/domains/assets/data-types';
import {
  SUPPORTED_DOCUMENT_MIMETYPES,
  SUPPORTED_IMAGE_MIMETYPES,
} from '@packages/domains/assets/data-types';

/**
 * Type for backward compatibility with FileRecord
 */
export type FileRecordLike = Pick<
  FileRecordType,
  'mimeType' | 'type' | 'origin' | 'versionedResource'
>;

/**
 * Union type accepting both legacy and new asset types
 */
export type AssetInput = FileRecordLike | Asset;

/**
 * Helper to extract contentType/mimeType from either asset type
 */
function getContentType(input: AssetInput): string | null | undefined {
  // FileRecord has mimeType directly accessible
  if ('mimeType' in input) {
    return input.mimeType;
  }
  // Asset type has contentType
  return input.contentType;
}

/**
 * Helper to extract asset type field from either asset type
 */
function getAssetType(input: AssetInput): string | null | undefined {
  return input.type;
}

/**
 * Check if asset is an image based on its MIME type
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's mimeType/contentType starts with 'image'
 *
 * @example
 * // With FileRecord
 * isImageAsset({mimeType: 'image/png'}) // true
 * isImageAsset({mimeType: 'video/mp4'}) // false
 *
 * // With Asset
 * isImageAsset({contentType: 'image/jpeg'}) // true
 */
export function isImageAsset(asset: AssetInput): boolean {
  const contentType = getContentType(asset);
  return contentType?.startsWith('image') ?? false;
}

/**
 * Check if asset is a Lottie animation
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's type is 'LOTTIE'
 *
 * @example
 * isLottieAsset({type: 'LOTTIE'}) // true
 * isLottieAsset({type: 'RIVE'}) // false
 * isLottieAsset({type: null}) // false
 */
export function isLottieAsset(asset: AssetInput): boolean {
  const type = getAssetType(asset);
  return type === 'LOTTIE';
}

/**
 * Check if asset is a supported document type
 *
 * Checks if asset has a supported document MIME type AND is not a Lottie file
 * (since JSON files can be Lottie animations).
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset is a supported document type (excluding Lottie)
 *
 * @example
 * isDocumentAsset({contentType: 'application/pdf'}) // true
 * isDocumentAsset({contentType: 'application/json', type: 'LOTTIE'}) // false
 * isDocumentAsset({contentType: 'image/png'}) // false
 */
export function isDocumentAsset(asset: AssetInput): boolean {
  const contentType = getContentType(asset);
  if (!contentType) return false;

  const isDocumentMimeType = contentType in SUPPORTED_DOCUMENT_MIMETYPES;

  // Exclude Lottie files (JSON files can be Lottie animations)
  return isDocumentMimeType && !isLottieAsset(asset);
}

/**
 * Check if asset is a video based on its MIME type
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's mimeType/contentType starts with 'video'
 *
 * @example
 * isVideoAsset({mimeType: 'video/mp4'}) // true
 * isVideoAsset({contentType: 'video/webm'}) // true
 * isVideoAsset({contentType: 'image/png'}) // false
 */
export function isVideoAsset(asset: AssetInput): boolean {
  const contentType = getContentType(asset);
  return contentType?.startsWith('video') ?? false;
}

/**
 * Check if asset is audio based on its MIME type
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's mimeType/contentType starts with 'audio'
 *
 * @example
 * isAudioAsset({mimeType: 'audio/mpeg'}) // true
 * isAudioAsset({contentType: 'audio/ogg'}) // true
 * isAudioAsset({contentType: 'video/mp4'}) // false
 */
export function isAudioAsset(asset: AssetInput): boolean {
  const contentType = getContentType(asset);
  return contentType?.startsWith('audio') ?? false;
}

/**
 * Check if asset is a Rive animation
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's type is 'RIVE'
 *
 * @example
 * isRiveAsset({type: 'RIVE'}) // true
 * isRiveAsset({type: 'LOTTIE'}) // false
 * isRiveAsset({type: null}) // false
 */
export function isRiveAsset(asset: AssetInput): boolean {
  const type = getAssetType(asset);
  return type === 'RIVE';
}

/**
 * Check if asset is an animated GIF
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's contentType is 'image/gif'
 *
 * @example
 * isGifAsset({contentType: 'image/gif'}) // true
 * isGifAsset({contentType: 'image/png'}) // false
 */
export function isGifAsset(asset: AssetInput): boolean {
  return getContentType(asset) === 'image/gif';
}

/**
 * Check if asset is from Figma
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's type is 'FIGMA'
 *
 * @example
 * isFigmaAsset({type: 'FIGMA'}) // true
 * isFigmaAsset({type: 'RIVE'}) // false
 * isFigmaAsset({type: null}) // false
 */
export function isFigmaAsset(asset: AssetInput): boolean {
  const type = getAssetType(asset);
  return type === 'FIGMA';
}

/**
 * Check if asset is a browser-supported image type
 *
 * Checks if the asset's MIME type is in the list of browser-supported image formats
 * (JPEG, PNG, GIF, SVG, BMP, WebP, AVIF).
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset's MIME type is browser-supported
 *
 * @example
 * isBrowserSupportedImage({mimeType: 'image/jpeg'}) // true
 * isBrowserSupportedImage({contentType: 'image/png'}) // true
 * isBrowserSupportedImage({contentType: 'image/tiff'}) // false
 */
export function isBrowserSupportedImage(asset: AssetInput): boolean {
  const contentType = getContentType(asset);
  if (!contentType) return false;
  return contentType in SUPPORTED_IMAGE_MIMETYPES;
}

/**
 * Check if asset is read-only
 *
 * Assets with an origin (from workspace libraries) are currently read-only.
 * More checks for readonly assets can be added here in the future.
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset has an origin (is from a library)
 *
 * @example
 * isReadOnlyAsset({origin: ['workspace', '123']}) // true
 * isReadOnlyAsset({origin: null}) // false
 */
export function isReadOnlyAsset(asset: AssetInput): boolean {
  return Boolean(asset.origin);
}

/**
 * Check if asset is from a library
 *
 * @param asset - The asset to check (supports both FileRecordType and Asset)
 * @returns true if the asset has a versionedResource (is from a library)
 *
 * @example
 * isLibraryAsset({versionedResource: '123'}) // true
 * isLibraryAsset({versionedResource: null}) // false
 * isLibraryAsset({}) // false
 */
export function isLibraryAsset(asset?: AssetInput): boolean {
  return Boolean(asset?.versionedResource);
}

/**
 * Human-readable display type for an asset, used in the metadata panel.
 */
export type AssetDisplayType =
  | 'Image'
  | 'Animation'
  | 'Video'
  | 'Document'
  | 'Audio';

/**
 * Return the human-readable display type for an asset.
 *
 * Resolution order:
 * 1. LOTTIE or RIVE → "Animation"
 * 2. image/gif → "Animation"
 * 3. audio MIME type or AUDIO asset type → "Audio"
 * 4. video MIME type → "Video"
 * 5. Supported document MIME type → "Document"
 * 6. image MIME type → "Image"
 * 7. Default fallback → "Image"
 *
 * @param asset - The asset to identify (supports both FileRecordType and Asset)
 * @returns The display type string
 *
 * @example
 * getAssetDisplayType({contentType: 'image/jpeg'}) // 'Image'
 * getAssetDisplayType({type: 'LOTTIE'}) // 'Animation'
 * getAssetDisplayType({contentType: 'image/gif'}) // 'Animation'
 * getAssetDisplayType({contentType: 'video/mp4'}) // 'Video'
 * getAssetDisplayType({contentType: 'application/pdf'}) // 'Document'
 * getAssetDisplayType({contentType: 'audio/mpeg'}) // 'Audio'
 */
export function getAssetDisplayType(asset: AssetInput): AssetDisplayType {
  if (isLottieAsset(asset) || isRiveAsset(asset) || isGifAsset(asset))
    return 'Animation';
  if (isAudioAsset(asset) || getAssetType(asset) === 'AUDIO') return 'Audio';
  if (isVideoAsset(asset)) return 'Video';
  if (isDocumentAsset(asset)) return 'Document';
  if (isImageAsset(asset)) return 'Image';
  return 'Image';
}
